<template>
	<BlockVisibilityObserver title="CALCULATOR">
		<section class="kova-lp-calc" :class="{ 'kova-lp-calc_flush-top': flushTop }">
			<div class="kova-lp-calc__inner">
				<!-- v-text, not an interpolation: the runs carry their own trailing spaces
				     and Vue's condense mode would add one more around a wrapped `{{ }}`. -->
				<h2 class="kova-lp-calc__title">
					<span
						v-for="(part, index) in titleParts"
						:key="index"
						:class="{ 'kova-lp-calc__title_accent': part.accent }"
						v-text="part.text"
					/>
				</h2>

				<div class="kova-lp-calc__row">
					<!-- A reveal slider. Both shots fill the whole box and the divider clips the
					     "before" layer, so every position shows one complete frame — never the
					     sliding crop a split-pane would give. -->
					<div class="kova-lp-calc__compare" :style="{ '--kova-lp-split': `${split}%` }">
						<img alt="" class="kova-lp-calc__photo" :src="compareAfter" />
						<img alt="" class="kova-lp-calc__photo kova-lp-calc__photo_before" :src="compareBefore" />

						<span
							class="kova-lp-calc__tag kova-lp-calc__tag_before"
							:class="{ 'kova-lp-calc__tag_faded': !beforeTagVisible }"
						>
							Before
						</span>
						<span
							class="kova-lp-calc__tag kova-lp-calc__tag_after"
							:class="{ 'kova-lp-calc__tag_faded': !afterTagVisible }"
						>
							After
						</span>

						<input
							v-model.number="split"
							aria-label="Before / After"
							class="kova-lp-calc__reveal"
							max="100"
							min="0"
							step="0.5"
							type="range"
						/>

						<span aria-hidden="true" class="kova-lp-calc__divider">
							<span class="kova-lp-calc__chevron kova-lp-calc__chevron_left">
								<InlineSvg :src="chevronBig" />
							</span>
							<span class="kova-lp-calc__chevron kova-lp-calc__chevron_right">
								<InlineSvg :src="chevronBig" />
							</span>
						</span>
					</div>

					<div class="kova-lp-calc__card">
						<p class="kova-lp-calc__card-title">{{ calculator.title }}</p>

						<div class="kova-lp-calc__weight">
							<p class="kova-lp-calc__weight-label">{{ calculator.weightLabel }}</p>
							<p class="kova-lp-calc__weight-value">
								<span>{{ weight }}</span>
								<span class="kova-lp-calc__weight-unit">{{ calculator.unit }}</span>
							</p>
						</div>

						<div class="kova-lp-calc__bar">
							<input
								v-model.number="weight"
								:aria-label="calculator.weightLabel"
								class="kova-lp-calc__range"
								:max="calculator.weight.max"
								:min="calculator.weight.min"
								:step="calculator.weight.step"
								type="range"
							/>
						</div>

						<p class="kova-lp-calc__result">
							<span class="kova-lp-calc__result-label">{{ calculator.resultLabel }}</span>
							<span class="kova-lp-calc__result-value">{{ projectedLoss }} {{ calculator.unit }}</span>
						</p>

						<div class="kova-lp-calc__promises">
							<span v-for="promise in calculator.promises" :key="promise.label" class="kova-lp-calc__promise">
								<InlineSvg
									class="kova-lp-calc__promise-icon"
									:src="promise.icon === 'cross' ? dsCloseIcon : dsCheckIcon"
								/>
								{{ promise.label }}
							</span>
						</div>

						<button class="kova-lp-calc__cta" type="button" @click="onCtaClick">{{ calculator.ctaLabel }}</button>
					</div>
				</div>

				<div class="kova-lp-calc__stats">
					<p class="kova-lp-calc__laurel">
						<img alt="" class="kova-lp-calc__laurel-leaf" :src="laurelLeft" />
						<span class="kova-lp-calc__laurel-copy">
							<span class="kova-lp-calc__laurel-value">{{ stats.value }}</span>
							<span class="kova-lp-calc__laurel-label">{{ stats.label }}</span>
						</span>
						<img alt="" class="kova-lp-calc__laurel-leaf" :src="laurelRight" />
					</p>

					<LandingKovaMarquee class="kova-lp-calc__perks">
						<ul class="kova-lp-calc__perk-list">
							<li v-for="perk in perks" :key="perk" class="kova-lp-calc__perk">
								<span class="kova-lp-calc__perk-badge"><InlineSvg :src="checkIcon" /></span>
								<span>{{ perk }}</span>
							</li>
						</ul>
					</LandingKovaMarquee>
				</div>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaMarquee from '@/modules/Landing/components/LandingKovaMarquee.vue'

import checkIcon from '@/assets/kova/landing/check.svg'
import chevronBig from '@/assets/kova/landing/chevron-big.svg'
import compareAfter from '@/assets/kova/landing/compare-after.jpg'
import compareBefore from '@/assets/kova/landing/compare-before.jpg'
import dsCheckIcon from '@/assets/icons/ds_check.svg'
import dsCloseIcon from '@/assets/kova/landing/ds-close.svg'
import laurelLeft from '@/assets/kova/landing/laurel-left.svg'
import laurelRight from '@/assets/kova/landing/laurel-right.svg'

export default {
	name: 'LandingKovaCalculator',

	components: { BlockVisibilityObserver, LandingKovaMarquee },

	props: {
		// { title, weightLabel, unit, resultLabel, ctaLabel, promises,
		//   weight: { min, max, step, default, lossRatio } }
		calculator: {
			type: Object,
			required: true,
		},

		// No gap of its own above the title on mobile: the USA frame starts the block right
		// under the first screen's ticker (6346:46447).
		flushTop: {
			type: Boolean,
			default: false,
		},

		perks: {
			type: Array,
			required: true,
		},

		// { value, label } — the laurel-wreath counter.
		stats: {
			type: Object,
			required: true,
		},

		// [{ text, accent }] — the headline, split so "Without the Hunger" can be green.
		titleParts: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { checkIcon, chevronBig, compareAfter, compareBefore, dsCheckIcon, dsCloseIcon, laurelLeft, laurelRight }
	},

	data() {
		return {
			// Percent of the box given to the "before" shot; the design opens even.
			split: 50,
			weight: this.calculator.weight.default,
		}
	},

	computed: {
		// The tags sit at 16…92 and 308…374 of the 390-wide box (5108:48961 / 5108:48963).
		// Each drops out once the divider reaches it, so a chip never gets cut mid-word.
		afterTagVisible() {
			return this.split < 79
		},

		beforeTagVisible() {
			return this.split > 24
		},

		// The design's own arithmetic: 220 lbs → 44 lbs is a flat 20%, matching the
		// "lose 20% of your body weight" claim in the hero.
		projectedLoss() {
			return Math.round(this.weight * this.calculator.weight.lossRatio)
		},
	},

	methods: {
		onCtaClick() {
			this.$emit('progressLoaderCompleted', 'calculator_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 5026:7572). The block sits on the landing's light page.
.kova-lp-calc {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-calc__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-32);
	padding-top: var(--size-48);
}

@media (max-width: 1023px) {
	.kova-lp-calc_flush-top .kova-lp-calc__inner {
		padding-top: 0;
	}
}

.kova-lp-calc__title {
	margin: 0;
	// The section itself is full-bleed for the before/after strip, so the heading
	// carries the page gutter on its own.
	padding-inline: var(--size-16);

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
	text-align: center;
	text-wrap: balance;
}

.kova-lp-calc__title_accent {
	color: var(--kova-lp-highlight);
}

.kova-lp-calc__row {
	display: flex;
	flex-direction: column;
	gap: var(--size-32);
}

// image-before-after (5108:48956) — one 390×390 box holding both shots.
.kova-lp-calc__compare {
	position: relative;
	overflow: hidden;

	aspect-ratio: 1;

	border-radius: var(--size-24);
	background: var(--kova-lp-surface);
}

// Both frames are pre-cropped to the same window, so `cover` needs no offsets.
.kova-lp-calc__photo {
	position: absolute;
	inset: 0;

	display: block;
	width: 100%;
	height: 100%;

	object-fit: cover;
	pointer-events: none;
}

.kova-lp-calc__photo_before {
	clip-path: inset(0 calc(100% - var(--kova-lp-split)) 0 0);
}

.kova-lp-calc__tag {
	position: absolute;
	top: var(--size-16);
	padding: var(--size-8) var(--size-16);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: 0.14px;

	border-radius: var(--size-32);
	color: var(--kova-lp-ink);
	background: var(--Fill-White);
	text-transform: uppercase;
	white-space: nowrap;
	transition: opacity 0.15s ease;
}

.kova-lp-calc__tag_before {
	left: var(--size-16);
}

.kova-lp-calc__tag_after {
	right: var(--size-16);
}

.kova-lp-calc__tag_faded {
	opacity: 0;
}

// Transparent on purpose: the divider is the visual, the input only carries the
// drag, touch and keyboard behaviour.
.kova-lp-calc__reveal {
	position: absolute;
	inset: 0;

	appearance: none;
	width: 100%;
	height: 100%;
	margin: 0;

	background: none;
	cursor: ew-resize;
	// `pan-y` was too strict: it declares the horizontal gesture off-limits, so the
	// range's own touch-drag never runs and only a tap (which still fires a click and
	// jumps the thumb) worked. `manipulation` keeps the page's vertical scroll — pan-y
	// is part of it — while letting the control take the horizontal drag. Nothing on
	// the landing scrolls horizontally, so allowing pan-x costs nothing.
	touch-action: manipulation;

	&::-webkit-slider-thumb {
		appearance: none;
		width: 1px;
		height: 100%;
	}

	&::-moz-range-thumb {
		width: 1px;
		height: 100%;

		border: none;
		background: none;
	}
}

// Rectangle 4254943 — a 4px white line centred on the split.
.kova-lp-calc__divider {
	position: absolute;
	top: 0;
	bottom: 0;
	left: var(--kova-lp-split);

	width: var(--size-4);
	margin-left: calc(var(--size-4) / -2);

	background: var(--Fill-White);
	pointer-events: none; // drags belong to the input underneath
}

.kova-lp-calc__reveal:focus-visible ~ .kova-lp-calc__divider {
	box-shadow: 0 0 0 var(--size-2) var(--kova-lp-brand);
}

.kova-lp-calc__chevron {
	position: absolute;
	top: 50%;

	display: flex;
	align-items: center;
	padding: var(--size-4);

	border-radius: 120px;
	color: var(--Fill-White);
	background: rgba(255, 255, 255, 0.4); // text/tertiary-white
	transform: translateY(-50%);
}

.kova-lp-calc__chevron :deep(svg) {
	display: block;
	width: var(--size-24);
	height: var(--size-24);
}

// 8px clear of the line on each side (5108:48967 / 5108:48969).
.kova-lp-calc__chevron_left {
	right: calc(100% + var(--size-8));
}

.kova-lp-calc__chevron_right {
	left: calc(100% + var(--size-8));
	transform: translateY(-50%) rotate(180deg);
}

.kova-lp-calc__card {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-16);
}

.kova-lp-calc__card-title {
	margin: 0;

	font-size: var(--size-24);
	font-weight: 500;
	line-height: 1.16;
	letter-spacing: -0.48px;

	color: var(--kova-lp-text);
	text-align: center;
}

.kova-lp-calc__weight {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-6);
}

.kova-lp-calc__weight-label {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-12);
	line-height: var(--size-16);

	color: var(--Text-Primary-100);
}

.kova-lp-calc__weight-value {
	display: flex;
	align-items: baseline;
	gap: var(--size-6);
	margin: 0;
	padding: var(--size-8) var(--size-12) var(--size-8) var(--size-16);

	font-size: var(--size-36);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.72px;

	border: 1px solid var(--kova-lp-hairline);
	border-radius: var(--size-16);
	color: var(--kova-lp-text);
	background: var(--kova-lp-surface);
}

.kova-lp-calc__weight-unit {
	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: normal;

	color: var(--Text-Primary-100);
}

.kova-lp-calc__bar {
	display: flex; // keeps the inline range input from adding baseline leading
	// Figma insets the mobile track to 350 of the 358 frame (4843:27426).
	padding-inline: var(--size-4);
	// The weight readout and the bar are one 12px-gap group inside the card's 24px
	// rhythm (4843:27419), so pull the bar back up by the difference.
	margin-top: -12px;
}

// Bar (4843:27425 / 4841:21190): 8px track, 32px knob that reads as a 4px ring.
.kova-lp-calc__range {
	appearance: none;
	width: 100%;
	height: var(--size-32);
	margin: 0;

	background: none;
	cursor: pointer;

	&::-webkit-slider-runnable-track {
		height: var(--size-8);
		border-radius: var(--size-12);
		background: var(--kova-lp-control);
	}

	&::-moz-range-track {
		height: var(--size-8);
		border-radius: var(--size-12);
		background: var(--kova-lp-control);
	}

	&::-webkit-slider-thumb {
		appearance: none;
		box-sizing: border-box;
		width: var(--size-32);
		height: var(--size-32);
		margin-top: -12px; // centres the 32px knob on the 8px track

		// white with a 32% #323653 wash over it (4841:21192) — no DS token
		border: 4px solid #bdbfc8;
		border-radius: var(--size-12);
		background: var(--Fill-White);
	}

	&::-moz-range-thumb {
		box-sizing: border-box;
		width: var(--size-32);
		height: var(--size-32);

		border: 4px solid #bdbfc8;
		border-radius: var(--size-12);
		background: var(--Fill-White);
	}
}

.kova-lp-calc__result {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: var(--size-72);
	margin: 0;
	padding: var(--size-16) var(--size-20);

	font-weight: 500;

	border-radius: var(--size-16);
	color: var(--kova-lp-brand);
	background: var(--kova-lp-result-bg);
}

.kova-lp-calc__result-label {
	// 4843:27435 breaks after "could"; the two words never share a line on mobile.
	max-width: 6em;

	font-size: var(--size-16);
	line-height: var(--size-20);
}

.kova-lp-calc__result-value {
	font-size: var(--size-32);
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-result-value);
	white-space: nowrap;
}

.kova-lp-calc__promises {
	display: flex;
	justify-content: center;
	gap: var(--size-8);
}

.kova-lp-calc__promise {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	padding: var(--size-4) var(--size-8);

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	border-radius: var(--size-8);
	color: var(--kova-lp-ink);
	box-shadow: inset 0 0 0 1px var(--kova-lp-hairline-soft); // inside stroke, as in Figma
	white-space: nowrap;
}

.kova-lp-calc__promise-icon :deep(svg) {
	display: block;
	width: var(--size-20);
	height: var(--size-20);
}

.kova-lp-calc__promise-icon {
	color: var(--Text-Primary-100);
}

.kova-lp-calc__cta {
	box-sizing: border-box;
	width: 100%;
	height: var(--size-52);
	// Same trick as the bar: the promises and the CTA are one 20px-gap group inside
	// the card's 24px rhythm (4843:27437).
	margin-top: -4px;
	padding: var(--size-16) var(--size-32);

	font-family: inherit;
	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);

	border: none;
	border-radius: var(--size-16);
	color: var(--Fill-White);
	background: var(--kova-lp-brand);
	cursor: pointer;
}

.kova-lp-calc__stats {
	display: flex;
	flex-direction: column-reverse; // mobile puts the perks above the wreath
	gap: var(--size-32);
}

.kova-lp-calc__laurel {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--size-20);
	margin: 0;
	padding-block: var(--size-16);
}

.kova-lp-calc__laurel-leaf {
	width: var(--size-28);
	height: var(--size-64);
}

.kova-lp-calc__laurel-copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
	width: 144px;

	font-family: var(--font-family);
	// Figma states 800, but fonts.ejs only loads Inter 400/500/600/700 — asking for
	// 800 just makes the browser synthesise a fake bold. 700 is the heaviest weight
	// that actually ships.
	font-weight: 700;

	color: var(--Text-Primary-100);
	text-align: center;
}

.kova-lp-calc__laurel-value {
	font-size: var(--size-28);
	line-height: var(--size-32);
}

.kova-lp-calc__laurel-label {
	font-size: var(--size-14);
	line-height: var(--size-16);

	text-transform: uppercase;
}

// The three perks overflow 390px, so mobile runs them as a ticker.
.kova-lp-calc__perks {
	--kova-lp-marquee-gap: var(--size-20);
	--kova-lp-marquee-duration: 24s;
	--kova-lp-marquee-fade: var(--size-24);
}

.kova-lp-calc__perk-list {
	display: flex;
	gap: var(--kova-lp-marquee-gap);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-calc__perk {
	display: flex;
	align-items: center;
	gap: var(--size-8);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.14px;

	color: var(--kova-lp-brand);
	white-space: nowrap;
}

// p-4 around a 16px icon slot (4843:26951) — the slot, not the vector, sets the size.
.kova-lp-calc__perk-badge {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-24);
	height: var(--size-24);
	padding: var(--size-4);

	border-radius: var(--size-16);
	color: var(--kova-lp-brand);
	background: var(--kova-lp-tint);
}

.kova-lp-calc__perk-badge :deep(svg) {
	display: block;
	width: 13.5px; // the 16px icon slot, cap-trimmed like the hero chips
	height: auto;

	transform: scaleY(-1);
}

// Desktop (Figma 5024:7322): two columns, and the calculator gains its own card.
@media (min-width: 1024px) {
	// The desktop frame puts this block on its own band and gives the calculator a card
	// in the contrasting colour (default: white band, page-coloured card); mobile drops
	// the card entirely.
	.kova-lp-calc {
		background: var(--kova-lp-band-desktop);
	}

	.kova-lp-calc__inner {
		box-sizing: border-box;
		// 1216px of content between Figma's 112px gutters (two 592 columns + 32).
		max-width: 1296px;
		margin-inline: auto;
		padding: var(--size-80) var(--size-40) 0;
		gap: var(--size-48);
	}

	.kova-lp-calc__title {
		max-width: 830px;
		margin-inline: auto;
		padding-inline: 0; // the section's own gutter takes over from the mobile one

		font-size: var(--size-56);
		line-height: 1.1;
		letter-spacing: -1.12px;
	}

	.kova-lp-calc__row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--size-32);
		align-items: stretch;
	}

	.kova-lp-calc__compare {
		aspect-ratio: 592 / 622;
		border-radius: var(--size-36);
	}

	.kova-lp-calc__tag {
		top: var(--size-20);
		padding: var(--size-8) var(--size-16);

		font-size: var(--size-16);
		letter-spacing: 0.16px;
	}

	.kova-lp-calc__tag_before {
		left: var(--size-20);
	}

	.kova-lp-calc__tag_after {
		right: var(--size-20);
	}

	.kova-lp-calc__chevron {
		padding: var(--size-8);
	}

	.kova-lp-calc__card {
		box-sizing: border-box;
		height: 100%;
		gap: var(--size-32);
		padding: var(--size-40);

		border-radius: var(--size-36);
		background: var(--kova-lp-calc-card-desktop);
	}

	.kova-lp-calc__card-title {
		font-size: var(--size-32);
		line-height: 1.2;
		letter-spacing: -0.64px;

		text-align: left;
	}

	.kova-lp-calc__weight {
		gap: var(--size-8);
	}

	.kova-lp-calc__weight-label {
		font-size: var(--size-14);
		line-height: var(--size-16);
	}

	.kova-lp-calc__weight-value {
		font-size: var(--size-40);
		letter-spacing: -0.8px;
	}

	.kova-lp-calc__bar {
		padding-inline: 0;
	}

	.kova-lp-calc__result {
		height: auto;
		padding: var(--size-20) var(--size-24);

		background: var(--kova-lp-result-bg-desktop);
	}

	.kova-lp-calc__result-label {
		max-width: none;

		font-size: var(--size-20);
		line-height: var(--size-32);
	}

	.kova-lp-calc__result-value {
		font-size: var(--size-40);
		letter-spacing: -0.8px;
	}

	.kova-lp-calc__promise-icon :deep(svg) {
		width: var(--size-24);
		height: var(--size-24);
	}

	.kova-lp-calc__cta {
		height: 90px; // 5026:7318 — between --size-88 and --size-96, kept exact
		margin-top: calc(-1 * var(--size-8)); // 24px group gap in a 32px card rhythm
		padding: 0 var(--size-24);

		font-size: var(--size-24);
		line-height: normal;

		border-radius: var(--size-28);
	}

	.kova-lp-calc__stats {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: var(--size-32);
		padding: var(--size-12) var(--size-32);
	}

	.kova-lp-calc__laurel {
		padding-block: 0;
	}

	// The row fits on desktop, so the ticker stands still and drops its duplicate
	// run rather than listing the perks twice.
	.kova-lp-calc__perks {
		--kova-lp-marquee-gap: var(--size-32);

		overflow: visible;
		mask-image: none;
	}

	.kova-lp-calc__perks :deep(.kova-lp-marquee__track) {
		animation: none;
	}

	.kova-lp-calc__perks :deep(.kova-lp-marquee__run) {
		padding-left: 0;
	}

	.kova-lp-calc__perks :deep(.kova-lp-marquee__run + .kova-lp-marquee__run) {
		display: none;
	}

	.kova-lp-calc__perk {
		gap: var(--size-12);

		font-size: var(--size-20);
		letter-spacing: -0.2px;
	}

	.kova-lp-calc__perk-badge {
		width: var(--size-32);
		height: var(--size-32);
		padding: var(--size-4);
	}

	.kova-lp-calc__perk-badge :deep(svg) {
		width: 20.25px; // the 24px icon slot
	}
}
</style>
